import { type SwatchHexValue, isValidHexColor } from '@/utils/color-utils';
import { isNullOrEmpty } from '@/utils/null-utils';
import {
  EmptyHexArrayError,
  InvalidHexColorError,
  MissingHexValueError
} from '../Swatch/errors';

/**
 * Validates hex value for a swatch and throws appropriate errors.
 *
 * @param hexValue - The hex value to validate (single color or array).
 * @param context - Optional context string for error messages (e.g., SKU).
 * @throws {MissingHexValueError} When hexValue is null, undefined, or empty.
 * @throws {EmptyHexArrayError} When hexValue is an empty array.
 * @throws {InvalidHexColorError} When hexValue contains invalid hex color format(s).
 */
export function validateHexValue(
  hexValue: SwatchHexValue | null | undefined
): asserts hexValue is SwatchHexValue {
  if (isNullOrEmpty(hexValue)) {
    throw new MissingHexValueError(
      `Swatch validation failed: Missing hexValue`
    );
  }

  // Validate array of colors (gradient)
  if (Array.isArray(hexValue)) {
    if (hexValue.length === 0) {
      throw new EmptyHexArrayError(`Swatch validation failed: Empty hex array`);
    }

    const invalidColors = hexValue.filter((color) => !isValidHexColor(color));
    if (invalidColors.length > 0) {
      throw new InvalidHexColorError(
        `Swatch validation failed: Invalid hex colors [${invalidColors.join(', ')}]`
      );
    }
  } else if (!isValidHexColor(hexValue)) {
    throw new InvalidHexColorError(
      `Swatch validation failed: Invalid hex color ${JSON.stringify(hexValue)}`
    );
  }
}
